import { useState, useEffect } from "react";
import { Container, Spinner } from "reactstrap";
import SelectionBar from "./SelectionBar";
import DateSelector from "../../components/public/DateSelector";
import TimeSelector from "../../components/public/TimeSelector";
import styles from "../../styles/public/date-time-page.module.css";
import api from "../../services/api";

const DateTimePage = ({ bookingData, updateBookingData, onEditStep, onTimeSelected, initialDate,
  maxDate, organization, displayPrices = true, editableSelections = true, isCreatingHold = false }) => {
  const [selectedDate, setSelectedDate] = useState(bookingData.date || null);
  const [selectedTime, setSelectedTime] = useState(bookingData.time || null);
  const [isSubmittingTime, setIsSubmittingTime] = useState(false);
  const [prices, setPrices] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {

    const fetchPrices = async () => {
      if (!bookingData.service) return;
      setError(null);

      try {
        const today = new Date(initialDate);
        today.setHours(0, 0, 0, 0);

        const getStartOfWeek = (date) => {
          const start = new Date(date);
          const day = start.getDay();
          const diff = (day === 0 ? -6 : 1) - day;
          start.setDate(start.getDate() + diff);
          return start;
        };

        const currentWeekStart = getStartOfWeek(today);

        const startDate = new Date(currentWeekStart);
        startDate.setDate(1); // First day of current month
        const endDate = new Date(currentWeekStart);
        endDate.setMonth(endDate.getMonth() + 2);

        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];

        const response = await api.get('/prices-by-date-range/', {
          params: {
            service: bookingData.service.id,
            start_date: startDateStr,
            end_date: endDateStr,
          },
          skipAuthRedirect: true,
        });

        setPrices(response.data);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch pricing data");
      }
    };

    fetchPrices();
  }, [bookingData.service, initialDate]);

  const fetchMorePrices = async (newStartDate, newEndDate) => {
    if (!bookingData.service) return;
    setError(null);

    try {
      const startDate = new Date(newStartDate);
      const endDate = new Date(newEndDate);

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await api.get('/prices-by-date-range/', {
        params: {
          service: bookingData.service.id,
          start_date: startDateStr,
          end_date: endDateStr,
        },
        skipAuthRedirect: true,
      });

      // Merge new prices with existing ones, avoiding duplicates
      setPrices(prevPrices => [...prevPrices, ...response.data.filter(newPrice =>
        !prevPrices.some(prevPrice => prevPrice.date === newPrice.date)
      )]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch additional pricing data");
    }
  };

  const handleDateSelect = (date) => {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    setSelectedDate(normalizedDate);
    updateBookingData({ date: normalizedDate });
  };

  const handleTimeSelect = async (time, price) => {
    // Hide the selector immediately to avoid a brief repaint while parent state catches up.
    setIsSubmittingTime(true);
    setSelectedTime(time);
    updateBookingData({ time, price });

    const success = await onTimeSelected(time, price);
    if (!success) {
      setIsSubmittingTime(false);
    }
  };

  useEffect(() => {
    if (!isCreatingHold) {
      setIsSubmittingTime(false);
    }
  }, [isCreatingHold]);

  useEffect(() => {
    // alert(bookingData.date);
    if (bookingData.date) {
      setSelectedDate(new Date(bookingData.date));
    }
    if (bookingData.time) {
      setSelectedTime(bookingData.time);
    }
  }, [bookingData.date, bookingData.time]);

  const formattedDate = selectedDate
    ? selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
    : null;

  return (
    <Container>
      <SelectionBar
        service={bookingData.service}
        date={formattedDate} // Pass formatted date string
        time={selectedTime}
        onEditStep={onEditStep}
        editable={editableSelections}
      />

      <h2 className={styles["datetime-title"]}>
        When would you like to play?
      </h2>

      {error && <div className={styles["error"]}>{error}</div>}

      <DateSelector
        selectedDate={selectedDate}
        maxDate={maxDate}
        onDateSelect={handleDateSelect}
        prices={prices}
        displayPrices={displayPrices}
        fetchMorePrices={fetchMorePrices}
      />

      {selectedDate && (
        <>
          {(isCreatingHold || isSubmittingTime) ? (
            <Container className="text-center my-5">
              <Spinner color="primary" className="mb-3" />
              <div>Reserving your selected time...</div>
            </Container>
          ) : (
            <TimeSelector
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              prices={prices}
              displayPrices={displayPrices}
              selectedService={bookingData.service}
              organization={organization}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default DateTimePage;
